import { Controller } from "@hotwired/stimulus"

// A styled replacement for a native <select> in the admin.
//
// The browser renders <option> with the OS widget — an opaque light popup on a
// dark admin, unstyleable. This draws the list ourselves with the same tokens as
// the rest of the app (bg-card, border-gray-100, hover:bg-lift), so it follows
// light/dark like everything else.
//
// The VALUE still lives in a real <input type="hidden">, so every form, query
// param and bit of JS reading `form.elements.foo.value` keeps working unchanged.
// The controller re-syncs its label on the hidden input's `change` event, which
// is what makes external writes (Edit prefill) and form.reset() visible.
export default class extends Controller {
  static targets = ["button", "label", "menu", "input", "option"]
  static values = { submitOnChange: Boolean }

  connect() {
    this._onDocClick = this.closeOnOutside.bind(this)
    this._onSync = this.syncFromInput.bind(this)
    document.addEventListener("click", this._onDocClick)
    this.inputTarget.addEventListener("change", this._onSync)
    // form.reset() restores the hidden input silently — no event fires, hence this.
    this.form = this.element.closest("form")
    if (this.form) {
      this._onReset = () => setTimeout(() => this.syncFromInput(), 0)
      this.form.addEventListener("reset", this._onReset)
    }
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    this.inputTarget.removeEventListener("change", this._onSync)
    if (this.form && this._onReset) this.form.removeEventListener("reset", this._onReset)
  }

  get isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    // One open list at a time, including across separate filter bars.
    document.querySelectorAll("[data-custom-select-open]").forEach((el) => {
      if (el !== this.element) el.dispatchEvent(new CustomEvent("custom-select:close"))
    })
    this.element.setAttribute("data-custom-select-open", "true")
    this.element.addEventListener("custom-select:close", () => this.close(), { once: true })

    this.menuTarget.classList.remove("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "true")
    requestAnimationFrame(() => {
      this.menuTarget.classList.remove("opacity-0", "scale-95")
      this.menuTarget.classList.add("opacity-100", "scale-100")
    })
  }

  close() {
    if (!this.isOpen) return
    this.element.removeAttribute("data-custom-select-open")
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.menuTarget.classList.remove("opacity-100", "scale-100")
    this.menuTarget.classList.add("opacity-0", "scale-95")
    setTimeout(() => this.menuTarget.classList.add("hidden"), 200)
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  select(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value
    if (value !== this.inputTarget.value) {
      this.inputTarget.value = value
      this.paint()
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
      if (this.submitOnChangeValue && this.form) this.form.requestSubmit()
    }
    this.close()
  }

  syncFromInput() {
    this.paint()
  }

  // Label + check mark follow the hidden input, never the other way round.
  paint() {
    const current = this.inputTarget.value
    this.optionTargets.forEach((option) => {
      const active = option.dataset.value === current
      option.classList.toggle("text-gray-900", active)
      option.classList.toggle("text-gray-500", !active)
      const check = option.querySelector("[data-check]")
      if (check) check.classList.toggle("invisible", !active)
      if (active) this.labelTarget.textContent = option.dataset.label
    })
    // A value with no matching option (stale param) leaves the first label showing.
    if (!this.optionTargets.some((o) => o.dataset.value === current)) {
      this.labelTarget.textContent = this.optionTargets[0]?.dataset.label || ""
    }
  }

  // Keyboard: the button is a real <button>, so Enter/Space open it for free.
  keydown(event) {
    if (event.key === "Escape" && this.isOpen) {
      event.stopPropagation()
      this.close()
      this.buttonTarget.focus()
      return
    }
    if (!["ArrowDown", "ArrowUp"].includes(event.key)) return
    event.preventDefault()
    if (!this.isOpen) return this.open()
    const options = this.optionTargets
    const index = options.indexOf(document.activeElement)
    const next = event.key === "ArrowDown"
      ? Math.min(index + 1, options.length - 1)
      : Math.max(index - 1, 0)
    options[index === -1 ? 0 : next]?.focus()
  }
}
